import { useEffect } from "react";
import { MapContainer, TileLayer, Marker, Popup, Polyline, Tooltip, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

function makeIcon(color, pulse = false) {
  const ring = pulse ? '<div style="position:absolute;inset:-8px;border-radius:50%;background:rgba(249,115,22,0.25);animation:routa-pulse 1.6s ease-out infinite"></div>' : "";
  return L.divIcon({
    className: "",
    html: `<div style="position:relative;width:16px;height:16px">${ring}<div style="position:relative;width:16px;height:16px;border-radius:50%;background:${color};border:3px solid #fff;box-shadow:0 0 0 1px rgba(0,0,0,0.25)"></div></div>`,
    iconSize: [16, 16],
    iconAnchor: [8, 8],
  });
}
const ICONS = {
  pickup: makeIcon("#0a0a0a"),
  dropoff: makeIcon("#f97316"),
  driver: makeIcon("#0a0a0a", true),
  point: makeIcon("#0a0a0a"),
};

function FitBounds({ points, includeDrivers }) {
  const map = useMap();
  const fit = points.filter((p) => includeDrivers || p.type !== "driver");
  const key = fit.map((p) => `${p.lat},${p.lng}`).join("|");
  useEffect(() => {
    const valid = fit.filter((p) => p.lat && p.lng);
    if (valid.length === 1) {
      map.setView([valid[0].lat, valid[0].lng], 12);
    } else if (valid.length > 1) {
      map.fitBounds(valid.map((p) => [p.lat, p.lng]), { padding: [40, 40] });
    }
  }, [key, map]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

function FollowDriver({ points }) {
  const map = useMap();
  const driver = points.find((p) => p.type === "driver" && p.lat && p.lng);
  const key = driver ? `${driver.lat},${driver.lng}` : "";
  useEffect(() => {
    if (driver) map.panTo([driver.lat, driver.lng], { animate: true, duration: 1 });
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

function Trail({ trail }) {
  const pts = (trail || []).filter((p) => p.lat && p.lng);
  if (pts.length < 2) return null;
  return pts.slice(1).map((p, i) => (
    <Polyline
      key={`${p.at || i}-${i}`}
      positions={[[pts[i].lat, pts[i].lng], [p.lat, p.lng]]}
      pathOptions={{ color: "#f97316", weight: 4, opacity: 0.15 + 0.85 * ((i + 1) / (pts.length - 1)), lineCap: "round" }}
    />
  ));
}

export default function MapView({ points = [], route = null, trails = [], height = 320, follow = false }) {
  const center = points[0] ? [points[0].lat, points[0].lng] : [49.2827, -123.1207];
  return (
    <div style={{ height }} className="w-full border border-gray-200 overflow-hidden" data-testid="map-view">
      <MapContainer center={center} zoom={11} style={{ height: "100%", width: "100%" }} scrollWheelZoom={true} doubleClickZoom={true} zoomControl={true}>
        <TileLayer
          attribution='&copy; <a href="https://www.mapbox.com/">Mapbox</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url={`https://api.mapbox.com/styles/v1/mapbox/streets-v12/tiles/{z}/{x}/{y}?access_token=${MAPBOX_TOKEN}`}
          tileSize={512}
          zoomOffset={-1}
        />
        {points.map((p, i) =>
          p.lat && p.lng ? (
            <Marker key={p.id || (p.type === "driver" ? `driver-${i}` : i)} position={[p.lat, p.lng]} icon={ICONS[p.type] || ICONS.point}>
              {p.label && <Popup>{p.label}</Popup>}
              {p.eta != null && (
                <Tooltip permanent direction="top" offset={[0, -10]} className="routa-eta-tip">
                  <span data-testid="driver-eta-badge">arriving in ~{p.eta} min</span>
                </Tooltip>
              )}
            </Marker>
          ) : null
        )}
        {trails.map((t, i) => <Trail key={i} trail={t} />)}
        {route && route.length > 1 && (
          <Polyline positions={route.map((p) => [p.lat, p.lng])} pathOptions={{ color: "#0a0a0a", weight: 2, dashArray: "6 6" }} />
        )}
        <FitBounds points={points} includeDrivers={!follow} />
        {follow && <FollowDriver points={points} />}
      </MapContainer>
    </div>
  );
}
